import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  faChartLine,
  faCoins,
  faCopy,
  faSackDollar,
  faTriangleExclamation,
  faUserPen,
  faUsers,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useGetResellerOverview, ResellerOverview } from "../../../hooks/Reseller/useGetResellerOverview"
import { useGetResellerProfile } from "../../../hooks/Reseller/useGetResellerProfile"
import { useResellerLogged } from "../../../context/useResellerContext"
import { convertToLocalMoney } from "../../../Objects/Generics/Helps"
import { CompleteResellerProfilePopup } from "../CompleteResellerProfilePopup"
import { EditResellerProfilePopup } from "../EditResellerProfilePopup"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { FinancialKpiCard } from "../../CashFlow/components/FinancialKpiCard"
import { FinancialChartCard } from "../../CashFlow/components/FinancialChartCard"
import { FinancialDonutChart } from "../../CashFlow/components/charts/FinancialDonutChart"

export const ResellerOverviewSection: React.FC = () => {
  const { t } = useTranslation("reseller")

  const { resellerProfile, setResellerProfile } = useResellerLogged()

  const { handleGetResellerProfile } = useGetResellerProfile()

  const { handleGetResellerOverview } = useGetResellerOverview()

  const [overview, setOverview] = useState<ResellerOverview | null>(null)

  const [isCompleteProfileOpen, setIsCompleteProfileOpen] = useState(false)

  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false)

  useEffect(() => {
    handleGetResellerOverview(setOverview)
  }, [])

  const refreshProfile = () => handleGetResellerProfile(setResellerProfile)

  const handleCopyReferralCode = async () => {
    if (!resellerProfile?.referralCode) return

    await navigator.clipboard.writeText(resellerProfile.referralCode)
  }

  if (!resellerProfile) {
    return null
  }

  return (
    <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
      <div className="space-y-4">
        {!resellerProfile.hasCompletedProfile && (
          <div className="flex flex-col items-start justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-700">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              {t("dashboard.profile_incomplete_banner")}
            </div>

            <button
              type="button"
              className="rounded-md bg-amber-500 px-4 py-2 text-sm font-bold text-white hover:bg-amber-600"
              onClick={() => setIsCompleteProfileOpen(true)}
              data-interaction-description-key="interactionDescriptions:pages.reseller.dashboard.button.completeProfile"
            >
              {t("dashboard.profile_incomplete_button")}
            </button>
          </div>
        )}

        <div className="flex flex-col gap-4 rounded-xl bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div>
            <span className="text-xs font-bold tracking-wide text-gray-400 uppercase">
              {t("dashboard.referral_code_label")}
            </span>

            <div className="mt-1 flex items-center gap-3">
              <span className="text-xl font-extrabold text-orange-600">{resellerProfile.referralCode}</span>

              <button
                type="button"
                onClick={handleCopyReferralCode}
                aria-label={t("dashboard.referral_code_label")}
                data-interaction-description-key="interactionDescriptions:pages.reseller.dashboard.button.copyReferralCode"
              >
                <FontAwesomeIcon icon={faCopy} className="text-gray-400 hover:text-gray-600" />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-100"
              onClick={() => setIsEditProfileOpen(true)}
              data-interaction-description-key="interactionDescriptions:pages.reseller.dashboard.button.editProfile"
            >
              <FontAwesomeIcon icon={faUserPen} className="text-xs" />
              {t("dashboard.edit_profile_button")}
            </button>

            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-100"
              onClick={() => setIsCompleteProfileOpen(true)}
              data-interaction-description-key="interactionDescriptions:pages.reseller.dashboard.button.editPaymentData"
            >
              <FontAwesomeIcon icon={faUserPen} className="text-xs" />
              {t("dashboard.edit_payment_data_button")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-4 min-[1800px]:grid-cols-5">
          <FinancialKpiCard
            title={t("overview.active_clients")}
            value={overview ? `${overview.activeClients}/${overview.totalClients}` : "—"}
            icon={faUsers}
            tone="brand"
          />

          <FinancialKpiCard
            title={t("overview.monthly_recurring_amount")}
            value={overview ? convertToLocalMoney(overview.monthlyRecurringAmount) : "—"}
            icon={faChartLine}
            tone="info"
          />

          <FinancialKpiCard
            title={t("overview.commission_paid")}
            value={overview ? convertToLocalMoney(overview.commissionPaid) : "—"}
            icon={faSackDollar}
            tone="positive"
          />

          <FinancialKpiCard
            title={t("overview.commission_pending")}
            value={overview ? convertToLocalMoney(overview.commissionPending) : "—"}
            icon={faCoins}
            tone="warning"
          />
        </div>

        {overview && (
          <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
            <FinancialChartCard title={t("overview.active_clients")} icon={faUsers}>
              <FinancialDonutChart
                data={[
                  { label: t("overview.active_clients"), value: overview.activeClients, color: "#10b981" },
                  {
                    label: t("overview.inactive_clients"),
                    value: Math.max(overview.totalClients - overview.activeClients, 0),
                    color: "#94a3b8",
                  },
                ]}
                ariaLabel={t("overview.active_clients")}
                emptyTitle={t("clients.empty")}
                formatValue={(value) => value.toLocaleString("pt-BR")}
              />
            </FinancialChartCard>

            <FinancialChartCard title={t("dashboard.sections.commissions.label")} icon={faSackDollar}>
              <FinancialDonutChart
                data={[
                  { label: t("overview.commission_paid"), value: overview.commissionPaid, color: "#10b981" },
                  { label: t("overview.commission_pending"), value: overview.commissionPending, color: "#f59e0b" },
                ]}
                ariaLabel={t("dashboard.sections.commissions.label")}
                emptyTitle={t("commissions.empty")}
                formatValue={convertToLocalMoney}
              />
            </FinancialChartCard>
          </div>
        )}

        {isCompleteProfileOpen && (
          <CompleteResellerProfilePopup
            profile={resellerProfile}
            onClose={() => setIsCompleteProfileOpen(false)}
            onSuccess={refreshProfile}
          />
        )}

        {isEditProfileOpen && (
          <EditResellerProfilePopup
            profile={resellerProfile}
            onClose={() => setIsEditProfileOpen(false)}
            onSuccess={refreshProfile}
          />
        )}
      </div>
    </Animate>
  )
}
